import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getDoctors, registerPatient, searchPatients } from '../utils/api';
import { printRegistrationSlip } from '../utils/printSlip';

const NAVY = '#0d2137';
const GREEN = '#1a6b3c';
const RED = '#c41e3a';
const BLUE = '#1d4ed8';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const CITIES = ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Multan', 'Peshawar', 'Quetta', 'Sialkot', 'Hyderabad', 'Other'];
const RELATION_TYPES = ['S/O', 'D/O', 'W/O', 'B/O'];

const initialForm = {
  full_name: '', relation_type: '', relation_name: '',
  gender: '', age_years: '', phone: '', cnic: '',
  country: 'Pakistan', city: '', blood_group: '',
  address: '', doctor_id: '', chief_complaint: '', existing_patient_id: ''
};

const initialVitals = {
  temperature: '', bp_systolic: '', bp_diastolic: '',
  pulse: '', spo2: '', weight: '', height: '', bmi: '',
  stethoscope_note: ''
};

// Web Bluetooth supported in Chrome/Edge only
const BT_SUPPORTED = typeof navigator !== 'undefined' && !!navigator.bluetooth;

// BLE service/characteristic UUIDs
const NORDIC_UART_SVC = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const NORDIC_UART_TX  = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';

function parseIEEE11073Float(dv, offset) {
  const raw = dv.getUint32(offset, true);
  const exp = raw >> 24;
  const man = raw & 0x00FFFFFF;
  const signedMan = man >= 0x800000 ? man - 0x1000000 : man;
  const signedExp = exp >= 0x80 ? exp - 0x100 : exp;
  return signedMan * Math.pow(10, signedExp);
}

function PageHeader() {
  return (
    <div style={{ background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '16px 24px 0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ minWidth: 150, display: 'flex', alignItems: 'center' }}>
            <img src="/careline-logo.png" alt="Careline" style={{ height: 70, objectFit: 'contain' }}
              onError={e => e.target.style.display = 'none'} />
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 27, fontWeight: 900, color: NAVY, letterSpacing: 0.5, lineHeight: 1.1 }}>DHA MEDICAL CENTRE</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 2 }}>In Collaboration with</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: RED, letterSpacing: 0.5 }}>CARELINE</div>
            <div style={{ display: 'inline-block', marginTop: 8, background: NAVY, color: '#fff', padding: '6px 28px', borderRadius: 25, fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>
              PATIENT REGISTRATION FORM
            </div>
          </div>
          <div style={{ minWidth: 150, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src="/dha-logo.png" alt="DHA" style={{ height: 88, objectFit: 'contain' }}
              onError={e => e.target.style.display = 'none'} />
          </div>
        </div>
        <div style={{ height: 1, background: '#e2e8f0', marginTop: 14 }} />
      </div>
    </div>
  );
}

function SectionHeader({ number, title, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: NAVY, color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0', marginBottom: 0 }}>
      <div style={{ width: 26, height: 26, borderRadius: '50%', background: RED, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 13, flexShrink: 0 }}>{number}</div>
      <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5 }}>{icon} {title}</span>
    </div>
  );
}

function BtButton({ label, state, onClick, disabled }) {
  const styles = {
    idle:       { bg: BLUE,    text: '#fff', border: BLUE },
    connecting: { bg: '#94a3b8', text: '#fff', border: '#94a3b8' },
    connected:  { bg: GREEN,   text: '#fff', border: GREEN },
    error:      { bg: RED,     text: '#fff', border: RED },
  };
  const s = styles[state] || styles.idle;
  const icons = { idle: '⬤', connecting: '⟳', connected: '✓', error: '⚠' };
  const labels = { idle: label, connecting: 'Connecting...', connected: 'Connected ✓', error: 'Retry' };
  return (
    <button type="button" onClick={onClick}
      disabled={disabled || state === 'connecting' || state === 'connected'}
      style={{
        padding: '8px 14px', background: s.bg, color: s.text,
        border: `1.5px solid ${s.border}`, borderRadius: 7, cursor: state === 'connecting' || state === 'connected' ? 'default' : 'pointer',
        fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5,
        opacity: disabled ? 0.4 : 1
      }}>
      <span style={{ fontSize: 10 }}>{icons[state]}</span>
      {labels[state]}
    </button>
  );
}

function VitalAlert({ value, type }) {
  let alert = null;
  if (type === 'bp' && value.systolic && value.diastolic) {
    if (parseInt(value.systolic) > 140 || parseInt(value.diastolic) > 90)
      alert = { msg: 'High BP', color: RED };
    else if (parseInt(value.systolic) < 90 || parseInt(value.diastolic) < 60)
      alert = { msg: 'Low BP', color: '#d97706' };
  }
  if (type === 'spo2' && value) {
    if (parseInt(value) < 94) alert = { msg: 'Low SpO2', color: RED };
  }
  if (type === 'temp' && value) {
    if (parseFloat(value) >= 38.0) alert = { msg: 'Fever', color: RED };
    else if (parseFloat(value) < 35.0) alert = { msg: 'Hypothermia', color: '#2563eb' };
  }
  if (!alert) return null;
  return (
    <span style={{ fontSize: 10, fontWeight: 800, color: alert.color, background: alert.color + '18', padding: '2px 7px', borderRadius: 10, marginLeft: 6 }}>
      ⚠ {alert.msg}
    </span>
  );
}

export default function Registration() {
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [vitals, setVitals] = useState(initialVitals);
  const [btState, setBtState] = useState({ bp: 'idle', oximeter: 'idle', temp: 'idle', scale: 'idle' });
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [loadedPatient, setLoadedPatient] = useState(null);

  useEffect(() => {
    getDoctors().then(r => setDoctors(r.data)).catch(() => toast.error('Failed to load doctors'));
  }, []);

  // Auto-calculate BMI when weight or height changes
  useEffect(() => {
    const w = parseFloat(vitals.weight);
    const h = parseFloat(vitals.height) / 100;
    if (w > 0 && h > 0) {
      setVitals(v => ({ ...v, bmi: (w / (h * h)).toFixed(1) }));
    } else {
      setVitals(v => ({ ...v, bmi: '' }));
    }
  }, [vitals.weight, vitals.height]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setV = (field) => (e) => setVitals(v => ({ ...v, [field]: e.target.value }));

  // ─── Import vitals from 73Health EVA clipboard ───────────────────────────
  const importFromEVA = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) {
        toast.warn('Clipboard is empty — please click "Copy all to clipboard" on EVA first');
        return;
      }
      const parsed = {};
      const hrMatch    = text.match(/HR\s*:\s*(\d+)/i);
      const spo2Match  = text.match(/SpO2\s*:\s*(\d+)/i);
      const sysMatch   = text.match(/SYS\s*:\s*(\d+)/i);
      const diaMatch   = text.match(/DIA\s*:\s*(\d+)/i);
      const pulseMatch = text.match(/PULSE\s*:\s*(\d+)/i);
      // Temperature: +36.85 or 36.85 format
      const tempMatch  = text.match(/[+]?(\d{2}\.\d{1,2})\s*[°℃]?/);

      if (hrMatch)    parsed.pulse       = hrMatch[1];
      if (spo2Match)  parsed.spo2        = spo2Match[1];
      if (sysMatch)   parsed.bp_systolic  = sysMatch[1];
      if (diaMatch)   parsed.bp_diastolic = diaMatch[1];
      if (pulseMatch) parsed.pulse        = pulseMatch[1]; // PULSE overrides HR for pulse field
      if (tempMatch && parseFloat(tempMatch[1]) > 25 && parseFloat(tempMatch[1]) < 50) {
        parsed.temperature = tempMatch[1];
      }

      if (Object.keys(parsed).length === 0) {
        toast.warn('No vitals found in clipboard — please copy from EVA again');
        return;
      }

      setVitals(v => ({ ...v, ...parsed }));
      toast.success('Vitals imported from EVA successfully');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Clipboard permission required — please allow clipboard access in browser');
      } else {
        toast.error('Import failed: ' + err.message);
      }
    }
  }, []);

  // ─── BLE: Oximeter (PC-60FW — Nordic UART) ───────────────────────────────
  const connectOximeter = useCallback(async () => {
    try {
      setBtState(s => ({ ...s, oximeter: 'connecting' }));
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [NORDIC_UART_SVC] }]
      });
      device.addEventListener('gattserverdisconnected', () => {
        setBtState(s => ({ ...s, oximeter: 'idle' }));
        toast.info('Oximeter disconnected');
      });
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(NORDIC_UART_SVC);
      const tx = await service.getCharacteristic(NORDIC_UART_TX);
      await tx.startNotifications();
      tx.addEventListener('characteristicvaluechanged', (e) => {
        const data = new Uint8Array(e.target.value.buffer);
        // PC-60FW packet: [0xaa, 0x55, type, len, PI_hi, PI_lo, SpO2_hi, SpO2, PR_hi, PR_lo, ...]
        if (data.length >= 10 && data[0] === 0xaa && data[1] === 0x55) {
          const spo2  = data[7];
          const pulse = (data[8] << 8) | data[9];
          if (spo2 > 0 && spo2 <= 100) {
            setVitals(v => ({ ...v, spo2: String(spo2), pulse: String(pulse) }));
          }
        }
      });
      setBtState(s => ({ ...s, oximeter: 'connected' }));
      toast.success('Oximeter connected — reading vitals...');
    } catch (err) {
      setBtState(s => ({ ...s, oximeter: 'error' }));
      if (err.name !== 'NotFoundError') toast.error('Oximeter: ' + err.message);
    }
  }, []);

  // ─── BLE: BP Monitor (U807 — Standard GATT Blood Pressure 0x1810) ────────
  const connectBP = useCallback(async () => {
    try {
      setBtState(s => ({ ...s, bp: 'connecting' }));
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ['blood_pressure'] }],
        optionalServices: ['blood_pressure']
      });
      device.addEventListener('gattserverdisconnected', () => {
        setBtState(s => ({ ...s, bp: 'idle' }));
        toast.info('BP monitor disconnected');
      });
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('blood_pressure');
      const char = await service.getCharacteristic('blood_pressure_measurement');
      await char.startNotifications();
      char.addEventListener('characteristicvaluechanged', (e) => {
        const dv = new DataView(e.target.value.buffer);
        const flags = dv.getUint8(0);
        // IEEE 11073 SFLOAT at bytes 1,3,5 = systolic, diastolic, mean
        const systolic  = Math.round(parseIEEE11073Float(dv, 1));
        const diastolic = Math.round(parseIEEE11073Float(dv, 3));
        const hasPulse  = (flags & 0x04) !== 0;
        const pulse     = hasPulse ? Math.round(parseIEEE11073Float(dv, 14)) : null;
        if (systolic > 0) {
          setVitals(v => ({
            ...v,
            bp_systolic: String(systolic),
            bp_diastolic: String(diastolic),
            ...(pulse ? { pulse: String(pulse) } : {})
          }));
        }
      });
      setBtState(s => ({ ...s, bp: 'connected' }));
      toast.success('BP monitor connected — take a reading...');
    } catch (err) {
      setBtState(s => ({ ...s, bp: 'error' }));
      if (err.name !== 'NotFoundError') toast.error('BP Monitor: ' + err.message);
    }
  }, []);

  // ─── BLE: Thermometer (CareLine — Standard GATT Health Thermometer 0x1809) ─
  const connectThermometer = useCallback(async () => {
    try {
      setBtState(s => ({ ...s, temp: 'connecting' }));
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ['health_thermometer'] }],
        optionalServices: ['health_thermometer']
      });
      device.addEventListener('gattserverdisconnected', () => {
        setBtState(s => ({ ...s, temp: 'idle' }));
        toast.info('Thermometer disconnected');
      });
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('health_thermometer');
      const char = await service.getCharacteristic('temperature_measurement');
      await char.startNotifications();
      char.addEventListener('characteristicvaluechanged', (e) => {
        const dv = new DataView(e.target.value.buffer);
        const flags = dv.getUint8(0);
        let temp = parseIEEE11073Float(dv, 1);
        if (flags & 0x01) temp = (temp - 32) * 5 / 9; // Fahrenheit → Celsius
        if (temp > 25 && temp < 50) {
          setVitals(v => ({ ...v, temperature: temp.toFixed(1) }));
        }
      });
      setBtState(s => ({ ...s, temp: 'connected' }));
      toast.success('Thermometer connected — take a reading...');
    } catch (err) {
      setBtState(s => ({ ...s, temp: 'error' }));
      if (err.name !== 'NotFoundError') toast.error('Thermometer: ' + err.message);
    }
  }, []);

  // ─── BLE: Weight Scale (Talec SmartScale — Standard GATT 0x181D) ─────────
  const connectScale = useCallback(async () => {
    try {
      setBtState(s => ({ ...s, scale: 'connecting' }));
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ['weight_scale'] }],
        optionalServices: ['weight_scale']
      });
      device.addEventListener('gattserverdisconnected', () => {
        setBtState(s => ({ ...s, scale: 'idle' }));
        toast.info('Scale disconnected');
      });
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('weight_scale');
      const char = await service.getCharacteristic('weight_measurement');
      await char.startNotifications();
      char.addEventListener('characteristicvaluechanged', (e) => {
        const dv = new DataView(e.target.value.buffer);
        const flags = dv.getUint8(0);
        let weight;
        if (flags & 0x01) {
          // Imperial: 0.01 lb units → kg
          weight = (dv.getUint16(1, true) * 0.01 * 0.453592).toFixed(1);
        } else {
          // SI: 0.005 kg units
          weight = (dv.getUint16(1, true) * 0.005).toFixed(1);
        }
        setVitals(v => {
          const h = parseFloat(v.height) / 100;
          const bmi = h > 0 ? (parseFloat(weight) / (h * h)).toFixed(1) : v.bmi;
          return { ...v, weight: String(weight), bmi };
        });
      });
      setBtState(s => ({ ...s, scale: 'connected' }));
      toast.success('Scale connected — step on the scale...');
    } catch (err) {
      setBtState(s => ({ ...s, scale: 'error' }));
      if (err.name !== 'NotFoundError') toast.error('Scale: ' + err.message);
    }
  }, []);

  const handleSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const r = await searchPatients(searchQ);
      setSearchResults(r.data);
      if (r.data.length === 0) toast.info('No patient found — fill form for new registration');
    } catch { toast.error('Search failed'); }
    setSearching(false);
  };

  const loadPatient = (p) => {
    setForm({
      full_name: p.full_name || '',
      relation_type: p.relation_type || '',
      relation_name: p.relation_name || p.father_name || '',
      gender: p.gender || '',
      age_years: p.age_years || '',
      phone: p.phone || '',
      cnic: p.cnic || '',
      country: p.country || 'Pakistan',
      city: p.city || '',
      blood_group: p.blood_group || '',
      address: p.address || '',
      doctor_id: p.doctor_id || '',
      chief_complaint: '',
      existing_patient_id: p.id || ''
    });
    if (p.doctor_id) setSelectedDoctor(doctors.find(d => String(d.id) === String(p.doctor_id)) || null);
    setSearchResults([]);
    setSearchQ('');
    setLoadedPatient(p);
    toast.success('Patient data loaded — update details if needed');
  };

  const handlePhone = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
    let val = digits;
    if (digits.length > 4) val = digits.slice(0, 4) + '-' + digits.slice(4);
    setForm(f => ({ ...f, phone: val }));
  };

  const handleCnic = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 13);
    let val = digits;
    if (digits.length > 12) val = digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12);
    else if (digits.length > 5) val = digits.slice(0, 5) + '-' + digits.slice(5);
    setForm(f => ({ ...f, cnic: val }));
  };

  const handleDoctorChange = (e) => {
    const id = e.target.value;
    setForm(f => ({ ...f, doctor_id: id }));
    setSelectedDoctor(doctors.find(d => String(d.id) === String(id)) || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error('Full name is required');
    if (!form.gender) return toast.error('Please select gender');
    if (!form.phone.trim()) return toast.error('Phone number is required');
    if (form.cnic && !/^\d{5}-\d{7}-\d$/.test(form.cnic)) return toast.error('CNIC format: 42301-1234567-8');
    setLoading(true);
    try {
      const payload = { ...form, doctor_id: form.doctor_id || null, ...vitals };
      const r = await registerPatient(payload);
      setSuccess({ ...r.data, vitals });
      toast.success('Patient registered successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm(initialForm);
    setVitals(initialVitals);
    setBtState({ bp: 'idle', oximeter: 'idle', temp: 'idle', scale: 'idle' });
    setSelectedDoctor(null);
    setSuccess(null);
    setSearchQ('');
    setSearchResults([]);
    setLoadedPatient(null);
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
        <PageHeader />
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '40px 32px', maxWidth: 520, margin: '0 auto', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: `2px solid ${GREEN}` }}>
            <div style={{ width: 64, height: 64, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, margin: '0 auto 16px' }}>✓</div>
            <h2 style={{ color: GREEN, marginBottom: 20, fontSize: 22 }}>Registration Successful</h2>
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16, textAlign: 'left', border: '1px solid #e2e8f0' }}>
              {[
                ['MRN', success.mrn],
                ['Visit Token', success.visit_token],
                ['Patient', success.patient?.full_name],
                success.doctor && ['Doctor', success.doctor.name],
                success.doctor && ['Fees', `PKR ${Number(success.doctor.fees || 0).toLocaleString()}`]
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b', fontSize: 13 }}>{label}</span>
                  <span style={{ fontWeight: 700, color: NAVY, fontSize: 13 }}>{value}</span>
                </div>
              ))}
              {/* Vitals summary on success screen */}
              {success.vitals && Object.values(success.vitals).some(v => v !== '') && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>VITALS RECORDED</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {success.vitals.bp_systolic && <span style={vitalChip}>BP {success.vitals.bp_systolic}/{success.vitals.bp_diastolic}</span>}
                    {success.vitals.spo2 && <span style={vitalChip}>SpO2 {success.vitals.spo2}%</span>}
                    {success.vitals.pulse && <span style={vitalChip}>Pulse {success.vitals.pulse}</span>}
                    {success.vitals.temperature && <span style={vitalChip}>Temp {success.vitals.temperature}°C</span>}
                    {success.vitals.weight && <span style={vitalChip}>Wt {success.vitals.weight}kg</span>}
                    {success.vitals.bmi && <span style={vitalChip}>BMI {success.vitals.bmi}</span>}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button style={{ flex: 1, padding: 12, background: NAVY, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
                onClick={() => printRegistrationSlip(success.patient, success.doctor, success.mrn, success.visit_token, success.vitals)}>
                Print Slip
              </button>
              <button style={{ flex: 1, padding: 12, background: GREEN, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
                onClick={resetForm}>
                + New Patient
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <PageHeader />

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 24px 40px', boxSizing: 'border-box', width: '100%', overflowX: 'hidden' }}>
        {/* Search Bar */}
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10 }}>Existing Patient Search</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }}
              placeholder="Search by Name / MRN / CNIC / Phone..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
            <button
              style={{ padding: '10px 24px', background: NAVY, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
              onClick={handleSearch} disabled={searching}>
              {searching ? '...' : 'Find'}
            </button>
            {loadedPatient && (
              <button
                type="button"
                style={{ padding: '10px 20px', background: '#0e6b8a', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
                onClick={() => printRegistrationSlip(loadedPatient, selectedDoctor, loadedPatient.mrn, '', null)}>
                Reprint
              </button>
            )}
          </div>
          {searchResults.length > 0 && (
            <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 7, overflow: 'hidden' }}>
              {searchResults.map(p => (
                <div key={p.id}
                  style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', background: '#fff' }}
                  onClick={() => loadPatient(p)}>
                  <div>
                    <div style={{ fontWeight: 700, color: NAVY }}>{p.full_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{p.mrn} · {p.phone}</div>
                  </div>
                  <div style={{ fontSize: 11, background: GREEN, color: '#fff', padding: '3px 10px', borderRadius: 20 }}>Load</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Section 1: Patient Details */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader number="1" title="Patient Details" icon="" />
            <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', borderTop: 'none' }}>
              <div style={grid2}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Full Name *</label>
                  <input style={inputStyle} value={form.full_name} onChange={set('full_name')} placeholder="Patient full name" required />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ ...fieldStyle, width: 110 }}>
                    <label style={labelStyle}>Relation</label>
                    <select style={inputStyle} value={form.relation_type} onChange={set('relation_type')}>
                      <option value="">--</option>
                      {RELATION_TYPES.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div style={{ ...fieldStyle, flex: 1 }}>
                    <label style={labelStyle}>Guardian Name</label>
                    <input style={inputStyle} value={form.relation_name} onChange={set('relation_name')} placeholder="Father / Husband name" />
                  </div>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Gender *</label>
                  <select style={inputStyle} value={form.gender} onChange={set('gender')}>
                    <option value="">Select gender</option>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Age (Years)</label>
                  <input style={inputStyle} type="number" min="0" max="150" value={form.age_years} onChange={set('age_years')} placeholder="Age" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Phone *</label>
                  <input style={inputStyle} value={form.phone} onChange={handlePhone} placeholder="03XX-XXXXXXX" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>CNIC</label>
                  <input style={inputStyle} value={form.cnic} onChange={handleCnic} placeholder="42301-XXXXXXX-X" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Country</label>
                  <input style={inputStyle} value={form.country} onChange={set('country')} placeholder="Pakistan" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>City</label>
                  <select style={inputStyle} value={form.city} onChange={set('city')}>
                    <option value="">Select city</option>
                    {CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Blood Group</label>
                  <select style={inputStyle} value={form.blood_group} onChange={set('blood_group')}>
                    <option value="">Select blood group</option>
                    {BLOOD_GROUPS.map(bg => <option key={bg}>{bg}</option>)}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Chief Complaint</label>
                  <input style={inputStyle} value={form.chief_complaint} onChange={set('chief_complaint')} placeholder="Reason for visit" />
                </div>
                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Address</label>
                  <input style={inputStyle} value={form.address} onChange={set('address')} placeholder="Home address" />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Doctor Selection */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader number="2" title="Doctor Selection" icon="" />
            <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', borderTop: 'none' }}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Select Doctor</label>
                <select style={{ ...inputStyle, fontSize: 15 }} value={form.doctor_id} onChange={handleDoctorChange}>
                  <option value="">-- Select a doctor --</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>{d.name} — {d.specialty}</option>
                  ))}
                </select>
              </div>
              {selectedDoctor && (
                <div style={{ marginTop: 16, background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a5c 100%)`, borderRadius: 10, padding: '16px 20px', color: '#fff' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[['Specialty', selectedDoctor.specialty], ['Room No.', selectedDoctor.room_number]].map(([label, value]) => (
                      <div key={label}>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{value || '—'}</div>
                      </div>
                    ))}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>Timing</div>
                      <div style={{ fontSize: 13 }}>{selectedDoctor.timing || '—'}</div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>Consultation Fees</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: '#4ade80' }}>PKR {Number(selectedDoctor.fees || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Vitals */}
          <div style={{ marginBottom: 24 }}>
            <SectionHeader number="3" title="Patient Vitals" icon="🩺" />
            <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', borderTop: 'none' }}>

              {/* EVA Import button */}
              <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button type="button" onClick={importFromEVA}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 13, boxShadow: '0 2px 8px rgba(234,88,12,0.35)' }}>
                  <span style={{ fontSize: 16 }}>⚡</span> Import from EVA
                </button>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Click "Copy all to clipboard" on EVA, then press this button</span>
              </div>

              {/* 4-column vital cards grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>

                {/* BP card */}
                <div style={vCard}>
                  <div style={vCardTop}>
                    <span style={vIcon}>❤️</span>
                    <span style={vLabel}>Blood Pressure</span>
                    <VitalAlert type="bp" value={{ systolic: vitals.bp_systolic, diastolic: vitals.bp_diastolic }} />
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 6 }}>
                    <input style={vInput} type="number" min="50" max="250"
                      value={vitals.bp_systolic} onChange={setV('bp_systolic')} placeholder="Sys" />
                    <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: 16 }}>/</span>
                    <input style={vInput} type="number" min="30" max="150"
                      value={vitals.bp_diastolic} onChange={setV('bp_diastolic')} placeholder="Dia" />
                  </div>
                  <div style={vUnit}>mmHg</div>
                  {BT_SUPPORTED && <BtButton label="Connect" state={btState.bp} onClick={connectBP} />}
                </div>

                {/* SpO2 card */}
                <div style={vCard}>
                  <div style={vCardTop}>
                    <span style={vIcon}>🫁</span>
                    <span style={vLabel}>SpO2</span>
                    <VitalAlert type="spo2" value={vitals.spo2} />
                  </div>
                  <input style={{ ...vInput, width: '100%', marginBottom: 6 }} type="number" min="50" max="100"
                    value={vitals.spo2} onChange={setV('spo2')} placeholder="98" />
                  <div style={vUnit}>%</div>
                  {BT_SUPPORTED && <BtButton label="Connect" state={btState.oximeter} onClick={connectOximeter} />}
                </div>

                {/* Pulse card */}
                <div style={vCard}>
                  <div style={vCardTop}>
                    <span style={vIcon}>💓</span>
                    <span style={vLabel}>Pulse</span>
                  </div>
                  <input style={{ ...vInput, width: '100%', marginBottom: 6 }} type="number" min="30" max="250"
                    value={vitals.pulse} onChange={setV('pulse')} placeholder="72" />
                  <div style={vUnit}>bpm</div>
                </div>

                {/* Temperature card */}
                <div style={vCard}>
                  <div style={vCardTop}>
                    <span style={vIcon}>🌡️</span>
                    <span style={vLabel}>Temperature</span>
                    <VitalAlert type="temp" value={vitals.temperature} />
                  </div>
                  <input style={{ ...vInput, width: '100%', marginBottom: 6 }} type="number" min="30" max="45" step="any"
                    value={vitals.temperature} onChange={setV('temperature')} placeholder="37.2" />
                  <div style={vUnit}>°C</div>
                  {BT_SUPPORTED && <BtButton label="Connect" state={btState.temp} onClick={connectThermometer} />}
                </div>

              </div>

              {/* Weight / Height / BMI row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>

                <div style={vCard}>
                  <div style={vCardTop}><span style={vIcon}>⚖️</span><span style={vLabel}>Weight</span></div>
                  <input style={{ ...vInput, width: '100%', marginBottom: 6 }} type="number" min="1" max="300" step="0.1"
                    value={vitals.weight} onChange={setV('weight')} placeholder="70" />
                  <div style={vUnit}>kg</div>
                  {BT_SUPPORTED && <BtButton label="Connect" state={btState.scale} onClick={connectScale} />}
                </div>

                <div style={vCard}>
                  <div style={vCardTop}><span style={vIcon}>📏</span><span style={vLabel}>Height</span></div>
                  <input style={{ ...vInput, width: '100%', marginBottom: 6 }} type="number" min="50" max="250"
                    value={vitals.height} onChange={setV('height')} placeholder="170" />
                  <div style={vUnit}>cm</div>
                </div>

                <div style={{ ...vCard, background: vitals.bmi
                    ? (parseFloat(vitals.bmi) < 18.5 ? '#eff6ff' : parseFloat(vitals.bmi) < 25 ? '#f0fdf4' : parseFloat(vitals.bmi) < 30 ? '#fffbeb' : '#fef2f2')
                    : '#f8fafc' }}>
                  <div style={vCardTop}><span style={vIcon}>📊</span><span style={vLabel}>BMI</span></div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: vitals.bmi
                      ? (parseFloat(vitals.bmi) < 18.5 ? '#1d4ed8' : parseFloat(vitals.bmi) < 25 ? GREEN : parseFloat(vitals.bmi) < 30 ? '#d97706' : RED)
                      : '#cbd5e1', marginBottom: 4, lineHeight: 1 }}>
                    {vitals.bmi || '—'}
                  </div>
                  <div style={{ ...vUnit, color: vitals.bmi
                      ? (parseFloat(vitals.bmi) < 18.5 ? '#1d4ed8' : parseFloat(vitals.bmi) < 25 ? GREEN : parseFloat(vitals.bmi) < 30 ? '#d97706' : RED)
                      : '#cbd5e1', fontWeight: 700 }}>
                    {vitals.bmi ? (parseFloat(vitals.bmi) < 18.5 ? 'Underweight' : parseFloat(vitals.bmi) < 25 ? 'Normal ✓' : parseFloat(vitals.bmi) < 30 ? 'Overweight' : 'Obese') : 'auto-calc'}
                  </div>
                </div>

                {/* Stethoscope notes — takes last column spanning */}
                <div style={{ ...vCard, gridColumn: '4 / 5' }}>
                  <div style={vCardTop}><span style={vIcon}>🩺</span><span style={vLabel}>Notes</span></div>
                  <textarea style={{ ...vInput, width: '100%', minHeight: 58, resize: 'none', fontSize: 11 }}
                    value={vitals.stethoscope_note} onChange={setV('stethoscope_note')}
                    placeholder="Breath sounds, heart sounds..." />
                </div>

              </div>

            </div>
          </div>

          {/* Submit */}
          <button type="submit" disabled={loading}
            style={{ width: '100%', padding: '15px', background: `linear-gradient(135deg, ${GREEN} 0%, #15803d 100%)`, color: '#fff', border: 'none', borderRadius: 10, fontSize: 17, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 12px rgba(26,107,60,0.3)', letterSpacing: 0.5 }}>
            {loading ? 'Registering...' : 'Register Patient'}
          </button>
        </form>

        {/* Footer */}
        <div style={{ marginTop: 32, background: NAVY, borderRadius: 10, padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ color: '#fff' }}>
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>DHA Medical Centre</div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.8 }}>
              DHA Karachi – Pakistan<br />
              📞 021-111-342-547 / 021-99266811-4
            </div>
          </div>
          <div style={{ color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: RED, fontWeight: 700, letterSpacing: 0.5 }}>CARELINE</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>One Heartbeat Away</div>
          </div>
          <div style={{ color: '#fff', textAlign: 'right' }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: RED, marginBottom: 6 }}>CARELINE</div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.8 }}>
              WhatsApp: +92 310 214 5333<br />
              🌐 www.thecareline.org<br />
              📧 contact@thecareline.org
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151' };
const inputStyle = { padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none', width: '100%', background: '#fff', boxSizing: 'border-box' };
const vitalChip = { fontSize: 11, fontWeight: 700, background: '#dbeafe', color: '#1e40af', padding: '3px 8px', borderRadius: 10 };
const vCard = { background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 };
const vCardTop = { display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 };
const vIcon = { fontSize: 14 };
const vLabel = { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 };
const vInput = { padding: '7px 10px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, fontWeight: 700, outline: 'none', background: '#fff', boxSizing: 'border-box', textAlign: 'center' };
const vUnit = { fontSize: 10, color: '#94a3b8', fontWeight: 600 };
